import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Wallet, ArrowUpRight, ArrowDownLeft, RefreshCw, AlertCircle, UserPlus, Search, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import AgentLayout from '../../components/layout/AgentLayout';

export const AgentFloatPage = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [floatBalance, setFloatBalance] = useState(0);
  const [floatCap, setFloatCap] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [requests, setRequests] = useState([]);
  
  // Dialogs
  const [topUpDialog, setTopUpDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [settlementDialog, setSettlementDialog] = useState(false);
  const [depositDialog, setDepositDialog] = useState(false);
  
  // Top-up form
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpReason, setTopUpReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Customer deposit form
  const [customerEmail, setCustomerEmail] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositCurrency, setDepositCurrency] = useState('ZAR');
  const [depositNote, setDepositNote] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchFloatData();
    fetchRequests();
  }, []);

  const fetchFloatData = async () => {
    try {
      // Fetch agent profile to get float balance
      const response = await fetch(`${API_URL}/api/agents/me/profile`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Read from nested float_wallet object
        setFloatBalance(data.float_wallet?.balance || 0);
        setFloatCap(data.float_wallet?.cap || 0);
      }

      // Fetch recent float transactions from float history
      const txResponse = await fetch(`${API_URL}/api/agents/me/float-history`, {
        credentials: 'include'
      });

      if (txResponse.ok) {
        const txData = await txResponse.json();
        setTransactions(txData.transactions || []);
      }

    } catch (error) {
      console.error('Failed to fetch float data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/agents/me/float-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };
  
  const handleTopUpRequest = async () => {
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agents/me/float-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(topUpAmount),
          reason: topUpReason
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Float top-up request submitted!');
        setTopUpDialog(false);
        setTopUpAmount('');
        setTopUpReason('');
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Request failed');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };
  
  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/user/directory?search=${encodeURIComponent(query)}&limit=10`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  };
  
  const handleCustomerDeposit = async () => {
    if (!selectedCustomer) {
      toast.error('Select a customer');
      return;
    }
    
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      // Use the new streamlined endpoint for direct deposits
      const response = await fetch(`${API_URL}/api/agents/me/deposits/create-for-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: selectedCustomer.id,
          currency: depositCurrency,
          amount: parseFloat(depositAmount),
          note: depositNote
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Deposited ${depositAmount} ${depositCurrency} to ${selectedCustomer.name || selectedCustomer.email}`);
        setDepositDialog(false);
        setSelectedCustomer(null);
        setCustomerEmail('');
        setDepositAmount('');
        setDepositNote('');
        fetchFloatData();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Deposit failed');
      }
    } catch (error) {
      toast.error('Deposit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'top_up': return '↓';
      case 'transfer_in': return '←';
      case 'transfer_out': return '→';
      case 'adjustment': return '⚙';
      case 'deduction': return '↑';
      default: return '•';
    }
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Float Wallet
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage your agent float balance for customer transactions
          </p>
        </div>

        {/* Float Balance Card */}
        <Card className="bg-gradient-to-br from-green-600 to-emerald-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90 mb-1">Current Float Balance</p>
                <h2 className="text-4xl font-bold mb-2">
                  {loading ? '...' : `R ${floatBalance.toFixed(2)}`}
                </h2>
                <p className="text-xs opacity-75">
                  Available for customer deposits/withdrawals • Cap: R {floatCap.toFixed(2)}
                </p>
              </div>
              <Wallet className="h-16 w-16 opacity-30" />
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setTopUpDialog(true)}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                  <ArrowDownLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Request Top-Up</h3>
                  <p className="text-sm text-muted-foreground">Add funds to float</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/agent/transactions')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                  <ArrowUpRight className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Transactions</h3>
                  <p className="text-sm text-muted-foreground">View all activity</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSettlementDialog(true)}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                  <RefreshCw className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold">Settlements</h3>
                  <p className="text-sm text-muted-foreground">View requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950 dark:to-green-950" onClick={() => setDepositDialog(true)}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-full">
                  <UserPlus className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">Deposit for Customer</h3>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">Credit customer account</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="transactions" className="w-full">
          <TabsList>
            <TabsTrigger value="transactions">Recent Transactions</TabsTrigger>
            <TabsTrigger value="requests">My Requests ({requests.length})</TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Float Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-muted-foreground py-8">Loading transactions...</p>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-muted-foreground">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((tx, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getTransactionIcon(tx.transaction_type)}</span>
                          <div>
                            <p className="font-medium capitalize">{tx.transaction_type?.replace('_', ' ')}</p>
                            <p className="text-sm text-muted-foreground">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.amount > 0 ? '+' : ''}R {Math.abs(tx.amount).toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">Balance: R {tx.balance_after?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Float Top-Up Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {requests.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-muted-foreground">No requests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requests.map((req, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">R {req.amount?.toFixed(2)}</p>
                          <p className="text-sm text-muted-foreground">{req.reason || 'No reason provided'}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(req.requested_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge className={getStatusColor(req.status)}>{req.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info Box */}
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  About Float Balance
                </p>
                <p className="text-blue-800 dark:text-blue-200">
                  Your float balance is the amount available for processing customer deposits and withdrawals. 
                  Keep adequate float to serve your customers. Request top-ups when balance is low.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Top-Up Request Dialog */}
      <Dialog open={topUpDialog} onOpenChange={setTopUpDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Float Top-Up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (ZAR)</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: R {floatBalance.toFixed(2)} | Cap: R {floatCap.toFixed(2)}
              </p>
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                placeholder="Why do you need this top-up?"
                value={topUpReason}
                onChange={(e) => setTopUpReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopUpDialog(false)}>Cancel</Button>
            <Button onClick={handleTopUpRequest} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Customer Deposit Dialog */}
      <Dialog open={depositDialog} onOpenChange={setDepositDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deposit for Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!selectedCustomer ? (
              <>
                <div>
                  <Label>Search Customer</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Email or name..."
                      className="pl-9"
                      value={customerEmail}
                      onChange={(e) => {
                        setCustomerEmail(e.target.value);
                        searchCustomers(e.target.value);
                      }}
                    />
                  </div>
                </div>
                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setSearchResults([]);
                        }}
                      >
                        <p className="font-medium">{customer.email}</p>
                        <p className="text-sm text-muted-foreground">{customer.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <Card className="bg-muted">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{selectedCustomer.email}</p>
                        <p className="text-sm text-muted-foreground">{selectedCustomer.name}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedCustomer(null)}>Change</Button>
                    </div>
                  </CardContent>
                </Card>
                
                <div>
                  <Label>Amount (ZAR)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>Note (Optional)</Label>
                  <Textarea
                    placeholder="Add a note..."
                    value={depositNote}
                    onChange={(e) => setDepositNote(e.target.value)}
                    rows={2}
                  />
                </div>
                
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
                  <p className="text-blue-900 dark:text-blue-100">
                    ⚠️ This will deduct R {depositAmount || '0.00'} from your float balance and credit the customer's account immediately.
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDepositDialog(false);
              setSelectedCustomer(null);
              setCustomerEmail('');
              setDepositAmount('');
              setSearchResults([]);
            }}>Cancel</Button>
            {selectedCustomer && (
              <Button onClick={handleCustomerDeposit} disabled={submitting}>
                {submitting ? 'Processing...' : `Deposit R ${depositAmount || '0.00'}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settlement Dialog */}
      <Dialog open={settlementDialog} onOpenChange={setSettlementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Float Requests Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {requests.filter(r => r.status === 'pending').length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No pending requests</p>
            ) : (
              requests.filter(r => r.status === 'pending').map((req, idx) => (
                <div key={idx} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">R {req.amount?.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground">{req.reason}</p>
                    </div>
                    <Badge className="bg-yellow-500">Pending</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setSettlementDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
};
